/**
 * TrailEngine
 * 
 * Canvas-based rendering engine for the "messy stroke" trail effect.
 * Supports both solid stroke and brush stamp rendering modes.
 */

import { ITrailEngine, TrailConfig } from '../types';
import { TrailPoint, TrailMode } from '../types';

// Default configuration
const DEFAULT_CONFIG: TrailConfig = {
  maxAge: 5000,           // ms before points fade
  stampSpacing: 8,        // px between brush stamps
  baseWidth: 12,          // base stroke width
  jitterRotation: 15,     // degrees of random rotation
  jitterOffset: 3,        // px of random position offset
  color: '#9B8ED8',       // lavender purple default
};

export class TrailEngine implements ITrailEngine {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private points: TrailPoint[] = [];
  private brushTexture: HTMLImageElement | null = null;
  private animationFrame: number | null = null;
  private mode: TrailMode = 'off';
  private config: TrailConfig;
  private dpr: number = 1;
  private running: boolean = false;
  
  // Fade state
  private fadeProgress: number = 1;
  private fadeDirection: 'in' | 'out' | null = null;

  constructor(config: Partial<TrailConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadBrushTexture();
  }

  /**
   * Load the brush tip texture
   */
  private loadBrushTexture(): void {
    const img = new Image();
    img.onload = () => {
      this.brushTexture = img;
    };
    img.onerror = () => {
      // Fallback: create a simple circular brush
      this.brushTexture = this.createFallbackBrush();
    };
    img.src = '/assets/brush-tip.png';
  }

  /**
   * Create a fallback circular brush texture
   */
  private createFallbackBrush(): HTMLImageElement {
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    
    // Create a soft circular gradient
    const gradient = ctx.createRadialGradient(
      size / 2, size / 2, 0,
      size / 2, size / 2, size / 2
    );
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.7)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    
    const img = new Image();
    img.src = canvas.toDataURL();
    return img;
  }

  /**
   * Mount the engine to a canvas element
   */
  mount(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.dpr = window.devicePixelRatio || 1;
    this.resizeCanvas();
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Handle window resize
   */
  private handleResize = (): void => {
    this.resizeCanvas();
  };

  /**
   * Resize canvas to match window size with device pixel ratio
   */
  private resizeCanvas(): void {
    if (!this.canvas) return;
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    
    if (this.ctx) {
      this.ctx.scale(this.dpr, this.dpr);
    }
  }

  /**
   * Add a point to the trail
   */
  addPoint(point: TrailPoint): void {
    if (this.mode !== 'on') return;
    
    // Calculate velocity if not provided
    if (point.velocity === undefined && this.points.length > 0) {
      const lastPoint = this.points[this.points.length - 1];
      const dx = point.x - lastPoint.x;
      const dy = point.y - lastPoint.y;
      const dt = point.t - lastPoint.t;
      point.velocity = dt > 0 ? Math.sqrt(dx * dx + dy * dy) / dt : 0;
    }
    
    // Default pressure
    if (point.pressure === undefined) {
      point.pressure = 1;
    }
    
    this.points.push(point);
    
    // Limit points buffer
    const maxPoints = 1000;
    if (this.points.length > maxPoints) {
      this.points = this.points.slice(-maxPoints);
    }
  }

  /**
   * Clear all points
   */
  clearPoints(): void {
    this.points = [];
    this.clearCanvas();
  }

  /**
   * Set the trail mode
   */
  setMode(mode: TrailMode): void {
    this.mode = mode;
    
    switch (mode) {
      case 'on':
        if (!this.running) {
          this.start();
        }
        this.fadeDirection = 'in';
        break;
        
      case 'off':
        this.fadeDirection = 'out';
        break;
        
      case 'clear':
        this.clearPoints();
        this.fadeProgress = 0;
        break;
        
      case 'fade':
        this.fadeDirection = 'out';
        break;
    }
  }

  /**
   * Get current mode
   */
  getMode(): TrailMode {
    return this.mode;
  }

  /**
   * Set trail color
   */
  setColor(color: string): void {
    this.config.color = color;
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<TrailConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Start the render loop
   */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.render();
  }

  /**
   * Stop the render loop
   */
  stop(): void {
    this.running = false;
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Main render loop
   */
  private render = (): void => {
    if (!this.running) return;
    
    // Update fade
    this.updateFade();
    
    // Clear and redraw
    this.clearCanvas();
    
    if (this.points.length > 1 && this.fadeProgress > 0) {
      this.renderBrushStamps();
    }
    
    // Remove old points
    this.pruneOldPoints();
    
    this.animationFrame = requestAnimationFrame(this.render);
  };

  /**
   * Update fade progress
   */
  private updateFade(): void {
    const fadeSpeed = 0.05;
    
    if (this.fadeDirection === 'in') {
      this.fadeProgress = Math.min(1, this.fadeProgress + fadeSpeed);
      if (this.fadeProgress >= 1) {
        this.fadeDirection = null;
      }
    } else if (this.fadeDirection === 'out') {
      this.fadeProgress = Math.max(0, this.fadeProgress - fadeSpeed);
      if (this.fadeProgress <= 0) {
        this.fadeDirection = null;
        if (this.mode === 'fade') {
          this.clearPoints();
        }
      }
    }
  }

  /**
   * Remove points older than maxAge
   */
  private pruneOldPoints(): void {
    const now = Date.now();
    const cutoff = now - this.config.maxAge;
    
    while (this.points.length > 0 && this.points[0].t < cutoff) {
      this.points.shift();
    }
  }

  /**
   * Clear the canvas
   */
  private clearCanvas(): void {
    if (!this.ctx || !this.canvas) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Render trail using brush stamps
   */
  private renderBrushStamps(): void {
    if (!this.ctx || !this.brushTexture || this.points.length < 2) return;
    
    const ctx = this.ctx;
    const { stampSpacing, baseWidth, jitterRotation, jitterOffset, color } = this.config;
    
    ctx.save();
    ctx.globalAlpha = this.fadeProgress;
    
    // Set composite mode for color tinting
    ctx.globalCompositeOperation = 'source-over';
    
    let lastStampX = this.points[0].x;
    let lastStampY = this.points[0].y;
    
    for (let i = 1; i < this.points.length; i++) {
      const point = this.points[i];
      
      // Calculate distance from last stamp
      const dx = point.x - lastStampX;
      const dy = point.y - lastStampY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < stampSpacing) continue;
      
      // Calculate number of stamps needed
      const numStamps = Math.floor(distance / stampSpacing);
      
      for (let j = 0; j < numStamps; j++) {
        const t = (j + 1) / numStamps;
        const x = lastStampX + dx * t;
        const y = lastStampY + dy * t;
        
        // Calculate stamp size based on velocity
        const velocity = point.velocity ?? 0;
        const velocityFactor = Math.max(0.5, 1 - velocity * 0.01);
        const size = baseWidth * velocityFactor * (point.pressure ?? 1);
        
        // Apply jitter
        const jitterX = (Math.random() - 0.5) * jitterOffset * 2;
        const jitterY = (Math.random() - 0.5) * jitterOffset * 2;
        const rotation = (Math.random() - 0.5) * jitterRotation * 2 * Math.PI / 180;
        
        // Calculate age-based opacity
        const age = Date.now() - point.t;
        const ageFactor = Math.max(0, 1 - age / this.config.maxAge);
        
        // Draw stamp
        ctx.save();
        ctx.translate(x + jitterX, y + jitterY);
        ctx.rotate(rotation);
        ctx.globalAlpha = this.fadeProgress * ageFactor * 0.8;
        
        // Tint the brush with color
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Add soft edge
        ctx.globalAlpha = this.fadeProgress * ageFactor * 0.4;
        ctx.beginPath();
        ctx.arc(0, 0, size / 2 + 2, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
      }
      
      lastStampX = point.x;
      lastStampY = point.y;
    }
    
    ctx.restore();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.handleResize);
    this.canvas = null;
    this.ctx = null;
    this.points = [];
    this.brushTexture = null;
  }
}

export default TrailEngine;
